// src/components/common/pageHeader.context.tsx

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useLayoutEffect,
} from 'react';
import { useLocation } from 'react-router-dom';

import type { PageHeaderSlots } from './pageHeader.types';

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

/**
 * PageHeaderContextValue
 *
 * Holds page-scoped slot state for AppPageHeader and exposes helpers to
 * update/reset it from route components.
 */
export type PageHeaderContextValue = {
  slots: PageHeaderSlots;
  setPageHeaderSlots: (next: PageHeaderSlots) => void;
  resetPageHeaderSlots: () => void;
};

/* ------------------------------------------------------------------ */
/* Context                                                            */
/* ------------------------------------------------------------------ */

/**
 * Default (fallback) context value.
 * Real values are provided by PageHeaderProvider.
 */
export const PageHeaderContext = createContext<PageHeaderContextValue>({
  slots: {},
  setPageHeaderSlots: () => {
    /* noop */
  },
  resetPageHeaderSlots: () => {
    /* noop */
  },
});

/* ------------------------------------------------------------------ */
/* Provider                                                           */
/* ------------------------------------------------------------------ */

/**
 * PageHeaderProvider
 *
 * Global provider that owns AppPageHeader "slots" (leading/actions, etc.).
 * Mount this once (typically under the Root layout) and let each page
 * populate the slots as needed.
 *
 * Behavior:
 * - Slots are page-scoped UI state and must not leak across routes.
 * - Slots are reset on every navigation (location.key changes).
 * - Reset is performed in a layout effect to avoid a single-frame "flash"
 *   where a previous page's header actions might briefly appear.
 */
export function PageHeaderProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  const [slots, setSlots] = useState<PageHeaderSlots>({});

  /**
   * Replace current header slots.
   * Passing an empty object clears all slots.
   */
  const setPageHeaderSlots = useCallback((next: PageHeaderSlots) => {
    setSlots(next ?? {});
  }, []);

  /**
   * Reset header slots to the initial empty state.
   */
  const resetPageHeaderSlots = useCallback(() => {
    setSlots({});
  }, []);

  /**
   * Reset slots on navigation.
   *
   * We use location.key (unique per navigation) to cover:
   * - pathname changes
   * - query string changes
   * - back/forward navigations
   *
   * useLayoutEffect prevents UI flicker where old actions might render for a frame.
   */
  useLayoutEffect(() => {
    resetPageHeaderSlots();
  }, [location.key, resetPageHeaderSlots]);

  const value = useMemo<PageHeaderContextValue>(
    () => ({
      slots,
      setPageHeaderSlots,
      resetPageHeaderSlots,
    }),
    [slots, setPageHeaderSlots, resetPageHeaderSlots],
  );

  return (
    <PageHeaderContext.Provider value={value}>
      {children}
    </PageHeaderContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/* Hook                                                               */
/* ------------------------------------------------------------------ */

/**
 * usePageHeader
 *
 * Public hook to access and control AppPageHeader slots.
 */
export function usePageHeader() {
  return useContext(PageHeaderContext);
}
